/**
 * ProductPage Controller
 * Manages product page functionality and integrates with theme events
 */
class ProductPage {
	constructor(options = {}) {
		this.productId = options.productId;
		this.currentVariantId = options.currentVariantId;
		this.formId = options.formId;

		this.form = document.getElementById(this.formId);
		this.variantIdInput = this.form?.querySelector("[data-variant-id]");
		this.quantityInput = this.form?.querySelector("[data-quantity-input]");
		this.addToCartBtn = this.form?.querySelector("[data-add-to-cart]");

		// AbortController for unified event cleanup
		this.abortController = new AbortController();

		// Store notification timer for cleanup
		this.notificationTimer = null;
		this.tableShadowObservers = [];

		this.init();
	}

	init() {
		this.bindEvents();
		this.initializeImageGallery();
		this.setupVariantHandling();
		this.setupQuantityControls();
		this.setupCartFunctionality();
		this.setupProseTableScrollHints();
		this.initializeFromURL();
		this.resetPurchaseButtons();

		// Initialize option availability based on current selections
		this.updateOptionAvailability();

		// Dispatch initialization event
		if (typeof ProductInitEvent !== "undefined") {
			document.dispatchEvent(
				new ProductInitEvent({
					productId: this.productId,
					currentVariantId: this.currentVariantId,
				}),
			);
		}
	}

	/**
	 * Cleanup method - call when navigating away or destroying the component
	 */
	destroy() {
		// Abort all event listeners registered with signal
		this.abortController?.abort();

		// Clear notification timer
		clearTimeout(this.notificationTimer);

		// Remove Lenis scroll listener if exists
		if (window.lenis && this._handleScroll) {
			window.lenis.off("scroll", this._handleScroll);
		}

		// Destroy Swiper instances
		if (typeof window.destroyProductSwipers === "function") {
			window.destroyProductSwipers();
		}

		this.tableShadowObservers.forEach((observer) => observer.disconnect());
		this.tableShadowObservers = [];

		// Remove any lingering notifications
		const notification = document.querySelector(".product-notification");
		notification?.remove();

		console.log("[ProductPage] Destroyed and cleaned up resources");
	}

	bindEvents() {
		const { signal } = this.abortController;

		// Listen for theme events
		if (typeof ThemeEvents !== "undefined") {
			document.addEventListener(
				ThemeEvents.variantSelected,
				(e) => this.handleVariantSelected(e),
				{ signal },
			);
			document.addEventListener(
				ThemeEvents.cartUpdate,
				(e) => this.handleCartUpdate(e),
				{ signal },
			);
			document.addEventListener(
				ThemeEvents.cartError,
				(e) => this.handleCartError(e),
				{ signal },
			);
		}

		// Listen for scroll events (Lenis integration)
		if (window.lenis) {
			// Store bound handler for cleanup
			this._handleScroll = this.handleScroll.bind(this);
			window.lenis.on("scroll", this._handleScroll);
		}

		// Safari/IOS may restore the page from bfcache keeping the disabled state from Buy Now
		window.addEventListener(
			"pageshow",
			(event) => {
				if (event.persisted) {
					this.resetPurchaseButtons();
					this.validateQuantity();
				}
			},
			{ signal },
		);
	}

	initializeImageGallery() {
		const { signal } = this.abortController;

		// Gallery swiper is already initialized in the snippet
		// Here we can add additional logic for gallery interactions
		const galleryImages = document.querySelectorAll(
			".product-main-swiper img",
		);

		galleryImages.forEach((img) => {
			img.addEventListener(
				"click",
				(e) => {
					this.openImageModal(e.target.src, e.target.alt);
				},
				{ signal },
			);
		});

		const singleImage = document.querySelector(".single-product-image img");
		if (singleImage) {
			singleImage.addEventListener(
				"click",
				(e) => {
					this.openImageModal(e.target.src, e.target.alt);
				},
				{ signal },
			);
		}
	}

	setupVariantHandling() {
		const { signal } = this.abortController;
		const variantInputs = document.querySelectorAll(
			".variant-option-input",
		);

		variantInputs.forEach((input) => {
			input.addEventListener(
				"change",
				(e) => {
					this.handleVariantChange(e);
				},
				{ signal },
			);
		});
	}

	setupQuantityControls() {
		const { signal } = this.abortController;
		const decreaseBtn = document.querySelector(".quantity-decrease");
		const increaseBtn = document.querySelector(".quantity-increase");

		if (decreaseBtn) {
			decreaseBtn.addEventListener(
				"click",
				() => {
					this.updateQuantity(-1);
				},
				{ signal },
			);
		}

		if (increaseBtn) {
			increaseBtn.addEventListener(
				"click",
				() => {
					this.updateQuantity(1);
				},
				{ signal },
			);
		}

		if (this.quantityInput) {
			// Handle manual input - validate on input
			this.quantityInput.addEventListener(
				"input",
				() => {
					this.updateButtonStates();
				},
				{ signal },
			);

			// Validate and correct value on blur
			this.quantityInput.addEventListener(
				"blur",
				() => {
					this.validateQuantity();
					this.dispatchQuantityUpdate();
				},
				{ signal },
			);

			// Handle Enter key to validate
			this.quantityInput.addEventListener(
				"keydown",
				(e) => {
					if (e.key === "Enter") {
						e.preventDefault();
						this.validateQuantity();
						this.dispatchQuantityUpdate();
						this.quantityInput.blur();
					}
				},
				{ signal },
			);

			// Initialize quantity validation to set correct button states based on inventory
			this.validateQuantity();
		}
	}

	setupCartFunctionality() {
		const { signal } = this.abortController;

		// Form submit listener as defense-in-depth — catches submission
		// paths that bypass the button click handler (e.g. Enter key,
		// programmatic submit, or clicks before JS initialized).
		if (this.form) {
			this.form.addEventListener(
				"submit",
				(e) => {
					e.preventDefault();
					this.addToCart();
				},
				{ signal },
			);
		}

		if (this.addToCartBtn) {
			this.addToCartBtn.addEventListener(
				"click",
				(e) => {
					e.preventDefault();
					this.addToCart();
				},
				{ signal },
			);
		}

		// Buy now button
		const buyNowBtn = document.querySelector("[data-buy-now]");
		if (buyNowBtn) {
			buyNowBtn.addEventListener(
				"click",
				() => {
					this.buyNow();
				},
				{ signal },
			);
		}
	}

	setupProseTableScrollHints() {
		const { signal } = this.abortController;
		const proseTables = document.querySelectorAll(".prose table");
		const canUseDragScroll = window.matchMedia(
			"(hover: hover) and (pointer: fine)",
		).matches;

		if (!proseTables.length) return;

		proseTables.forEach((table) => {
			let isPointerDown = false;
			let isDragging = false;
			let startX = 0;
			let startScrollLeft = 0;

			const updateShadows = () => {
				const maxScrollLeft = table.scrollWidth - table.clientWidth;
				const canScroll = maxScrollLeft > 2;
				const isAtStart = table.scrollLeft <= 1;
				const isAtEnd = table.scrollLeft >= maxScrollLeft - 1;

				if (canUseDragScroll && canScroll) {
					table.setAttribute("data-can-drag-scroll", "true");
				} else {
					table.removeAttribute("data-can-drag-scroll");
				}

				if (!canScroll) {
					table.removeAttribute("data-shadow-left");
					table.removeAttribute("data-shadow-right");
					table.classList.remove("is-grabbing");
					isPointerDown = false;
					isDragging = false;
					return;
				}

				table.setAttribute(
					"data-shadow-left",
					isAtStart ? "false" : "true",
				);
				table.setAttribute(
					"data-shadow-right",
					isAtEnd ? "false" : "true",
				);
			};

			table.addEventListener("scroll", updateShadows, {
				signal,
				passive: true,
			});

			if (canUseDragScroll) {
				table.addEventListener(
					"pointerdown",
					(event) => {
						if (event.button !== 0) return;

						const maxScrollLeft =
							table.scrollWidth - table.clientWidth;
						if (maxScrollLeft <= 2) return;

						isPointerDown = true;
						isDragging = false;
						startX = event.clientX;
						startScrollLeft = table.scrollLeft;
					},
					{ signal },
				);

				table.addEventListener(
					"pointermove",
					(event) => {
						if (!isPointerDown) return;

						const deltaX = event.clientX - startX;
						if (!isDragging && Math.abs(deltaX) > 4) {
							isDragging = true;
							table.classList.add("is-grabbing");
						}

						if (!isDragging) return;

						table.scrollLeft = startScrollLeft - deltaX;
						event.preventDefault();
					},
					{ signal },
				);

				const stopDragging = () => {
					if (!isPointerDown && !isDragging) return;
					isPointerDown = false;
					isDragging = false;
					table.classList.remove("is-grabbing");
				};

				table.addEventListener("pointerup", stopDragging, { signal });
				table.addEventListener("pointercancel", stopDragging, {
					signal,
				});
				table.addEventListener("pointerleave", stopDragging, {
					signal,
				});
			}

			window.addEventListener("resize", updateShadows, { signal });

			if (typeof ResizeObserver !== "undefined") {
				const observer = new ResizeObserver(() => {
					updateShadows();
				});
				observer.observe(table);
				this.tableShadowObservers.push(observer);
			}

			requestAnimationFrame(updateShadows);
		});
	}

	initializeFromURL() {
		const urlParams = new URLSearchParams(window.location.search);
		const hasVariantParam = urlParams.has("variant");
		const urlVariant = this.getVariantFromURL();

		if (urlVariant) {
			this.selectVariantByObject(urlVariant);
			this.handleVariantChange();
			return;
		}

		const selectedVariant = this.findSelectedVariant();
		const fallbackVariant =
			selectedVariant || window.productVariants?.[0] || null;

		if (fallbackVariant) {
			this.currentVariantId = fallbackVariant.id;
			this.updateProductInfo(fallbackVariant);
			if (
				!hasVariantParam ||
				urlParams.get("variant") !== String(fallbackVariant.id)
			) {
				this.updateURLWithVariant(fallbackVariant.id);
			}
		}
	}

	getVariantFromURL() {
		const urlParams = new URLSearchParams(window.location.search);
		const variantId = urlParams.get("variant");
		if (!variantId || !window.productVariants) return null;

		return window.productVariants.find(
			(variant) => variant.id == variantId,
		);
	}

	selectVariantByObject(variant) {
		if (!variant) return false;
		const optionNames = window.productOptions || [];
		const variantOptions = {};

		variant.options?.forEach((optionValue, index) => {
			const optionName = optionNames[index];
			if (optionName && optionValue) {
				variantOptions[optionName] = optionValue;
			}
		});

		let allOptionsSelected = true;
		Object.keys(variantOptions).forEach((optionName) => {
			const optionValue = variantOptions[optionName];
			const input = document.querySelector(
				`input[name="options[${optionName}]"][value="${optionValue}"]`,
			);

			if (input) {
				input.checked = true;
			} else {
				allOptionsSelected = false;
			}
		});

		return allOptionsSelected;
	}

	updateURLWithVariant(variantId) {
		const url = new URL(window.location);
		url.searchParams.set("variant", variantId);

		// Update URL without triggering page reload
		window.history.replaceState(
			{ variantId: variantId },
			"",
			url.toString(),
		);
	}

	handleVariantChange(e) {
		const selectedVariant = this.findSelectedVariant();

		// Update option availability states based on current selections
		this.updateOptionAvailability();

		if (selectedVariant) {
			this.updateProductInfo(selectedVariant);

			// Update URL with variant parameter
			this.updateURLWithVariant(selectedVariant.id);

			// Dispatch variant selected event
			if (typeof ThemeEvents !== "undefined") {
				document.dispatchEvent(
					new CustomEvent(ThemeEvents.variantSelected, {
						detail: {
							productId: this.productId,
							variantId: selectedVariant.id,
							variant: selectedVariant,
						},
					}),
				);
			}
		}
	}

	/**
	 * Update the availability state of all option inputs based on current selections.
	 * For each option group, checks which values can form a valid available variant
	 * when combined with the current selections from other option groups.
	 * Note: Out-of-stock options remain clickable but have visual styling to indicate unavailability.
	 */
	updateOptionAvailability() {
		const productVariants = window.productVariants || [];
		const productOptions = window.productOptions || [];

		// Gather current selections for each option
		const currentSelections = {};
		document
			.querySelectorAll(".variant-option-input:checked")
			.forEach((input) => {
				const optionName = input.name.match(/options\[(.*?)\]/)?.[1];
				if (optionName) {
					currentSelections[optionName] = input.value;
				}
			});

		// For each option group, determine which values are available
		productOptions.forEach((optionName, optionIndex) => {
			const optionInputs = document.querySelectorAll(
				`input[name="options[${optionName}]"]`,
			);

			optionInputs.forEach((input) => {
				const optionValue = input.value;

				// Check if there's an available variant with this option value
				// combined with the current selections of OTHER options
				const isAvailable = productVariants.some((variant) => {
					// Variant must be available
					if (!variant.available) return false;

					// Variant must have this option value at the correct index
					if (variant.options[optionIndex] !== optionValue)
						return false;

					// Check if variant matches current selections for OTHER options
					return productOptions.every(
						(otherOptionName, otherIndex) => {
							// Skip the current option being evaluated
							if (otherIndex === optionIndex) return true;

							// If we have a selection for this other option, variant must match
							const selectedValue =
								currentSelections[otherOptionName];
							if (selectedValue) {
								return (
									variant.options[otherIndex] ===
									selectedValue
								);
							}

							// No selection for this option, so any value is acceptable
							return true;
						},
					);
				});

				// Update data-unavailable attribute (not disabled, so options remain clickable)
				if (isAvailable) {
					input.removeAttribute("data-unavailable");
				} else {
					input.setAttribute("data-unavailable", "true");
				}

				// Update visual state of the option button/swatch
				const parentLabel = input.closest("label");
				if (parentLabel) {
					const optionButton =
						parentLabel.querySelector(".option-button");
					const colorSwatch =
						parentLabel.querySelector(".color-swatch");
					const colorName = parentLabel.querySelector(".color-name");

					if (optionButton) {
						if (isAvailable) {
							optionButton.classList.remove(
								"bx-opacity-50",
								"bx-line-through",
							);
							optionButton.classList.add(
								"hover:bx-border-theme-pink",
							);
						} else {
							optionButton.classList.add(
								"bx-opacity-50",
								"bx-line-through",
							);
							optionButton.classList.remove(
								"hover:bx-border-theme-pink",
							);
						}
					}

					if (colorSwatch) {
						if (isAvailable) {
							colorSwatch.classList.remove("bx-opacity-50");
						} else {
							colorSwatch.classList.add("bx-opacity-50");
						}
					}

					if (colorName) {
						if (isAvailable) {
							colorName.classList.remove("bx-line-through");
						} else {
							colorName.classList.add("bx-line-through");
						}
					}
				}
			});
		});
	}

	findSelectedVariant() {
		const productVariants = window.productVariants || [];
		const selectedOptions = {};

		document
			.querySelectorAll(".variant-option-input:checked")
			.forEach((input) => {
				const optionName = input.name.match(/options\[(.*?)\]/)?.[1];
				if (optionName) {
					selectedOptions[optionName] = input.value;
				}
			});

		return productVariants.find((variant) => {
			if (!variant.available) return false;
			const optionKeys = Object.keys(selectedOptions);
			return optionKeys.every((key) =>
				variant.options.includes(selectedOptions[key]),
			);
		});
	}

	updateProductInfo(variant) {
		// Update price
		const priceElement = document.querySelector("[data-product-price]");
		if (
			priceElement &&
			variant.price !== undefined &&
			variant.price !== null
		) {
			const formattedPrice = this.getVariantMoney(variant.id, "price");
			if (formattedPrice !== null) {
				priceElement.textContent = formattedPrice;
			}
		}

		// Update compare price and savings
		const comparePriceElement = document.querySelector(
			"[data-compare-price]",
		);
		const savingsElement = document.querySelector("[data-savings]");
		const hasDiscount =
			variant.compare_at_price &&
			variant.compare_at_price > variant.price;

		if (hasDiscount) {
			// Show compare price
			if (comparePriceElement) {
				const formattedComparePrice = this.getVariantMoney(
					variant.id,
					"compare_at_price",
				);
				if (formattedComparePrice !== null) {
					comparePriceElement.textContent = formattedComparePrice;
				}
				comparePriceElement.style.display = "inline";
			}

			// Show savings
			if (savingsElement) {
				const formattedSavings = this.getVariantMoney(
					variant.id,
					"savings",
				);
				const saveLabel =
					savingsElement.getAttribute("data-save-label") || "Save";
				if (formattedSavings !== null) {
					savingsElement.textContent = `${saveLabel} ${formattedSavings}`;
				}
				savingsElement.style.display = "inline";
			}
		} else {
			// Hide compare price and savings
			if (comparePriceElement) {
				comparePriceElement.style.display = "none";
			}
			if (savingsElement) {
				savingsElement.style.display = "none";
			}
		}

		// Update SKU
		const skuElement = document.querySelector("[data-variant-sku]");
		if (skuElement) {
			skuElement.textContent = variant.sku || "";
		}

		// Update variant ID
		if (this.variantIdInput) {
			this.variantIdInput.value = variant.id;
		}

		// Update inventory data for quantity controls
		this.updateInventoryData(variant);

		// Update availability (pass variant for inventory-aware stock status)
		this.updateAvailability(variant.available, variant);

		this.currentVariantId = variant.id;
	}

	/**
	 * Update inventory data attributes when variant changes
	 */
	updateInventoryData(variant) {
		if (!this.quantityInput) return;

		// Get inventory info from the separate inventory mapping
		const inventoryInfo =
			window.productVariantsInventory?.[variant.id] || {};
		const inventoryQty = inventoryInfo.inventory_quantity || 0;
		const inventoryPolicy = inventoryInfo.inventory_policy || "deny";

		// Update data attributes
		this.quantityInput.dataset.inventoryQuantity = inventoryQty;
		this.quantityInput.dataset.inventoryPolicy = inventoryPolicy;

		// Calculate max using the centralized method (considers both inventory and cart limit)
		const max = this.getMaxQuantity();
		this.quantityInput.setAttribute("max", max);

		// Reset quantity to 1 when variant changes (or adjust if current value exceeds new max)
		const currentQty = parseInt(this.quantityInput.value) || 1;
		if (currentQty > max) {
			this.quantityInput.value = Math.max(1, max);
		}

		// Revalidate quantity and update button states
		this.validateQuantity();
	}

	updateAvailability(available, variant = null) {
		const stockStatus = document.querySelector("[data-stock-status]");
		const addToCartBtn = this.addToCartBtn;
		const buttonText = document.querySelector("[data-button-text]");

		if (stockStatus) {
			if (!available) {
				stockStatus.innerHTML =
					'<span class="bx-flex bx-items-center bx-text-[14px] laptop:bx-text-[16px] bx-text-red-500"><span class="bx-w-[8px] bx-h-[8px] bx-bg-red-500 bx-rounded-full bx-mr-[8px]"></span>Unavailable</span>';
			} else {
				// Get inventory quantity from the variant inventory data
				const variantId = variant?.id || this.currentVariantId;
				const inventoryInfo =
					window.productVariantsInventory?.[variantId] || {};
				const inventoryQty = inventoryInfo.inventory_quantity;
				const inventoryPolicy = inventoryInfo.inventory_policy;

				// Check for low stock (inventory_quantity > 0 and <= 10)
				// Note: if inventory_policy is 'continue', we may not show low stock warning
				if (
					inventoryQty !== undefined &&
					inventoryQty > 0 &&
					inventoryQty <= 10 &&
					inventoryPolicy !== "continue"
				) {
					stockStatus.innerHTML = `<span class="bx-flex bx-items-center bx-text-[14px] laptop:bx-text-[16px] bx-text-orange-500"><span class="bx-w-[8px] bx-h-[8px] bx-bg-orange-500 bx-rounded-full bx-mr-[8px]"></span>Low Stock (${inventoryQty} left)</span>`;
				} else {
					stockStatus.innerHTML =
						'<span class="bx-flex bx-items-center bx-text-[14px] laptop:bx-text-[16px] bx-text-green-600"><span class="bx-w-[8px] bx-h-[8px] bx-bg-green-600 bx-rounded-full bx-mr-[8px]"></span>In Stock</span>';
				}
			}
		}

		if (addToCartBtn) {
			addToCartBtn.disabled = !available;

			if (buttonText) {
				buttonText.textContent = available
					? "Add to Cart"
					: "Unavailable";
			}
		}

		// Update quantity input readonly state based on availability
		if (this.quantityInput) {
			if (available) {
				this.quantityInput.removeAttribute("readonly");
			} else {
				this.quantityInput.setAttribute("readonly", "");
			}
		}

		// Update PayPal/dynamic checkout button state
		const paymentButtons = document.querySelector("[data-payment-buttons]");
		if (paymentButtons) {
			if (available) {
				paymentButtons.classList.remove("payment-button-disabled");
			} else {
				paymentButtons.classList.add("payment-button-disabled");
			}
		}
	}

	updateQuantity(change) {
		if (!this.quantityInput) return;

		const currentValue = parseInt(this.quantityInput.value) || 1;
		const min = parseInt(this.quantityInput.getAttribute("min")) || 1;
		const max = this.getMaxQuantity();

		const newValue = Math.max(min, Math.min(max, currentValue + change));

		if (newValue !== currentValue) {
			this.quantityInput.value = newValue;
			this.validateQuantity();
			this.dispatchQuantityUpdate();
		}

		// Always update button states after any quantity change attempt
		this.updateButtonStates();
	}

	/**
	 * Get max quantity based on current variant inventory and cart limit settings
	 */
	getMaxQuantity() {
		const inventoryQtyRaw = this.quantityInput?.dataset.inventoryQuantity;
		const inventoryPolicy = this.quantityInput?.dataset.inventoryPolicy;

		// Calculate inventory-based max
		let inventoryMax = 9999;
		if (inventoryPolicy !== "continue") {
			// If inventoryQuantity is defined (even if 0), use it; otherwise default to 9999
			const inventoryQty =
				inventoryQtyRaw !== undefined && inventoryQtyRaw !== ""
					? parseInt(inventoryQtyRaw)
					: 9999;
			inventoryMax = Math.max(1, inventoryQty);
		}

		// Check cart quantity limit from theme settings
		const cartLimit = window.cartQuantityLimit;
		let cartMax = 9999;
		if (cartLimit?.enabled && cartLimit?.limit > 0) {
			cartMax = cartLimit.limit;
		}

		// Return the smaller of inventory max and cart limit
		return Math.min(inventoryMax, cartMax);
	}

	/**
	 * Update button states based on current input value (without correcting the value)
	 */
	updateButtonStates() {
		if (!this.quantityInput) return;

		const value = parseInt(this.quantityInput.value) || 0;
		const min = parseInt(this.quantityInput.getAttribute("min")) || 1;
		const max = this.getMaxQuantity();

		const decreaseBtn = document.querySelector(".quantity-decrease");
		const increaseBtn = document.querySelector(".quantity-increase");

		if (decreaseBtn) decreaseBtn.disabled = value <= min;
		if (increaseBtn) increaseBtn.disabled = value >= max;
	}

	validateQuantity() {
		if (!this.quantityInput) return;

		// Handle empty, NaN, or invalid input - default to min
		let value = parseInt(this.quantityInput.value);
		if (isNaN(value) || value < 1) {
			value = 1;
		}

		const min = parseInt(this.quantityInput.getAttribute("min")) || 1;
		const max = this.getMaxQuantity();

		if (value < min) value = min;
		if (value > max) value = max;

		// Always set the corrected value
		this.quantityInput.value = value;

		// Reuse updateButtonStates to avoid duplicate logic
		this.updateButtonStates();
	}

	dispatchQuantityUpdate() {
		if (typeof ThemeEvents !== "undefined") {
			document.dispatchEvent(
				new CustomEvent(ThemeEvents.quantitySelectorUpdate, {
					detail: {
						productId: this.productId,
						variantId: this.currentVariantId,
						quantity: parseInt(this.quantityInput?.value) || 1,
					},
				}),
			);
		}
	}

	async addToCart() {
		if (!this.form) return;

		// Check for buy-together block
		if (this._hasBuyTogetherProducts()) {
			return this._addToCartWithBuyTogether();
		}

		const formData = new FormData(this.form);

		// Disable Buy Now button to prevent concurrent actions
		const buyNowBtn = document.querySelector("[data-buy-now]");
		if (buyNowBtn) buyNowBtn.disabled = true;

		// Show loading state
		this.setLoadingState(true);

		try {
			const response = await fetch("/cart/add.js", {
				method: "POST",
				body: formData,
				headers: {
					"X-Requested-With": "XMLHttpRequest",
				},
			});

			// Handle API errors (may return HTML captcha page)
			if (response.status >= 400) {
				this.handleRequestError(response);
				return;
			}

			const data = await response.json();

			if (!response.ok) {
				throw data; // Throw the original error object directly
			}

			// Fetch the latest cart data to update item count
			const cartResponse = await fetch("/cart.js");
			const cartData = await cartResponse.json();

			// Dispatch cart update event - success notices are handled by handleCartUpdate
			if (typeof ThemeEvents !== "undefined") {
				document.dispatchEvent(
					new CustomEvent(ThemeEvents.cartUpdate, {
						detail: {
							item: data,
							action: "add",
							productId: data.product_id,
							variantId: data.variant_id,
							quantity: parseInt(this.quantityInput?.value) || 1,
							item_count: cartData.item_count,
						},
					}),
				);
			} else {
				// If ThemeEvents is unavailable, show a direct notification
				this.showNotification("Product added to cart!", "success");
			}
		} catch (error) {
			console.error("error", error);

			// Normalize error messages from both plain objects and Error instances
			const errorMessage = this.normalizeCartErrorMessage(
				error.description || error.message || "Unknown error",
			);

			// Dispatch cart error event - notices are handled by handleCartError
			if (typeof ThemeEvents !== "undefined") {
				document.dispatchEvent(
					new CustomEvent(ThemeEvents.cartError, {
						detail: {
							error: errorMessage,
							action: "add",
							productId: this.productId,
							variantId: this.currentVariantId,
						},
					}),
				);
			} else {
				// If ThemeEvents is unavailable, show a direct notification
				this.showNotification(errorMessage, "error");
			}
		} finally {
			this.setLoadingState(false);

			// Re-enable Buy Now button
			const buyNowBtn = document.querySelector("[data-buy-now]");
			if (buyNowBtn) buyNowBtn.disabled = false;
		}
	}

	/**
	 * Check if buy-together block is present and has checked products.
	 */
	_hasBuyTogetherProducts() {
		const btContainer = document.querySelector("[data-buy-together]");
		if (!btContainer) return false;

		const btProducts = btContainer.querySelectorAll("[data-bt-product]");
		if (!btProducts.length) return false;

		const btDontNeed = btContainer.querySelector("[data-bt-dont-need]");
		if (btDontNeed && btDontNeed.checked) return false;

		return Array.from(btProducts).some(
			(p) => p.querySelector("[data-bt-checkbox]")?.checked,
		);
	}

	/**
	 * Build product-data object for the main product shown in the modal.
	 */
	_getMainProductData(quantity) {
		const productMedia = window.productMedia || [];
		const featuredMedia =
			productMedia.find((m) => m.media_type === "image") ||
			productMedia[0];
		const image =
			featuredMedia?.src || featuredMedia?.preview_image?.src || "";

		const titleEl =
			document.querySelector(".product-info-section h1") ||
			document.querySelector('[property="og:title"]');
		const title = titleEl?.textContent?.trim() || titleEl?.content || "";

		const variants = window.productVariants || [];
		const currentVariant = variants.find(
			(v) => v.id === this.currentVariantId,
		);
		const variantTitle =
			currentVariant && currentVariant.title !== "Default Title"
				? currentVariant.title
				: "";

		const price =
			this.getVariantMoney(this.currentVariantId, "price") || "";

		return { image, title, variant_title: variantTitle, price, quantity };
	}

	/**
	 * Add main product + checked buy-together products to cart via JSON multi-item API.
	 */
	async _addToCartWithBuyTogether() {
		const btContainer = document.querySelector("[data-buy-together]");
		const btProducts = btContainer.querySelectorAll("[data-bt-product]");
		const mainQuantity = parseInt(this.quantityInput?.value) || 1;

		const items = [{ id: this.currentVariantId, quantity: mainQuantity }];

		// Collect product info for modal display
		const productsForModal = [this._getMainProductData(mainQuantity)];

		for (const productEl of btProducts) {
			const checkbox = productEl.querySelector("[data-bt-checkbox]");
			if (!checkbox || !checkbox.checked) continue;

			const variantId = parseInt(
				productEl.dataset.btSelectedVariantId,
				10,
			);
			if (!variantId) continue;

			// Validate variant availability
			let variants = [];
			try {
				variants = JSON.parse(productEl.dataset.btVariants || "[]");
			} catch (e) {
				/* ignore parse errors */
			}

			const variant = variants.find((v) => v.id === variantId);
			if (!variant || !variant.available) {
				this.showNotification(
					"One or more selected variants are currently unavailable. Please check your selections.",
					"error",
				);
				return;
			}

			items.push({ id: variantId, quantity: 1 });

			// Collect product info for modal
			productsForModal.push({
				image: productEl.dataset.btImage || "",
				title: productEl.dataset.btTitle || "",
				variant_title:
					variant.title !== "Default Title" ? variant.title : "",
				price: variant.price,
				quantity: 1,
			});
		}

		// Disable Buy Now button to prevent concurrent actions
		const buyNowBtn = document.querySelector("[data-buy-now]");
		if (buyNowBtn) buyNowBtn.disabled = true;

		this.setLoadingState(true);

		try {
			const response = await fetch("/cart/add.js", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"X-Requested-With": "XMLHttpRequest",
				},
				body: JSON.stringify({ items }),
			});

			if (response.status >= 400) {
				this.handleRequestError(response);
				return;
			}

			const data = await response.json();

			if (!response.ok) {
				throw data;
			}

			// Fetch updated cart data
			const cartResponse = await fetch("/cart.js");
			const cartData = await cartResponse.json();

			// Dispatch cart update with multi-item data
			if (typeof ThemeEvents !== "undefined") {
				document.dispatchEvent(
					new CustomEvent(ThemeEvents.cartUpdate, {
						detail: {
							items: data.items || items,
							products: productsForModal,
							action: "add",
							productId: this.productId,
							variantId: this.currentVariantId,
							quantity: mainQuantity,
							item_count: cartData.item_count,
							is_multi: true,
						},
					}),
				);
			} else {
				this.showNotification("Products added to cart!", "success");
			}
		} catch (error) {
			console.error("Buy-together cart error:", error);

			const errorMessage = this.normalizeCartErrorMessage(
				error.description || error.message || "Unknown error",
			);

			if (typeof ThemeEvents !== "undefined") {
				document.dispatchEvent(
					new CustomEvent(ThemeEvents.cartError, {
						detail: {
							error: errorMessage,
							action: "add",
							productId: this.productId,
							variantId: this.currentVariantId,
						},
					}),
				);
			} else {
				this.showNotification(errorMessage, "error");
			}
		} finally {
			this.setLoadingState(false);

			const buyNowBtn = document.querySelector("[data-buy-now]");
			if (buyNowBtn) buyNowBtn.disabled = false;
		}
	}

	async buyNow() {
		const buyNowBtn = document.querySelector("[data-buy-now]");
		const buyNowText = document.querySelector("[data-buy-now-text]");
		const buyNowLoading = document.querySelector("[data-buy-now-loading]");

		if (!this.form) return;

		// Show processing state immediately
		if (buyNowText) buyNowText.textContent = "Processing...";
		if (buyNowLoading) buyNowLoading.classList.remove("bx-hidden");
		if (buyNowBtn) buyNowBtn.disabled = true;

		// Disable Add to Cart button to prevent concurrent actions
		if (this.addToCartBtn) this.addToCartBtn.disabled = true;

		try {
			const formData = new FormData(this.form);

			// Clear cart first
			await fetch("/cart/clear.js", {
				method: "POST",
			});

			const response = await fetch("/cart/add.js", {
				method: "POST",
				body: formData,
			});

			// Handle API errors (may return HTML captcha page)
			if (response.status >= 400) {
				this.handleRequestError(response);
				return;
			}

			const data = await response.json();

			if (!response.ok) {
				throw data; // Keep behavior consistent with addToCart
			}

			// Redirect to checkout immediately without showing success notification
			window.location.href = "/checkout";
		} catch (error) {
			console.error("Error with buy now:", error);

			// Normalize errors, consistent with addToCart
			const errorMessage = this.normalizeCartErrorMessage(
				error.description ||
					error.message ||
					"Error processing purchase",
			);

			// Reset button state on error
			if (buyNowText) buyNowText.textContent = "Buy Now";
			if (buyNowLoading) buyNowLoading.classList.add("bx-hidden");
			if (buyNowBtn) buyNowBtn.disabled = false;

			// Re-enable Add to Cart button (check variant availability)
			const currentVariant = window.productVariants?.find(
				(v) => v.id == this.currentVariantId,
			);
			const isAvailable = currentVariant?.available ?? true;
			if (this.addToCartBtn) this.addToCartBtn.disabled = !isAvailable;

			// Dispatch cart error event
			if (typeof ThemeEvents !== "undefined") {
				document.dispatchEvent(
					new CustomEvent(ThemeEvents.cartError, {
						detail: {
							error: errorMessage,
							action: "buy_now",
							productId: this.productId,
							variantId: this.currentVariantId,
						},
					}),
				);
			} else {
				this.showNotification(errorMessage, "error");
			}
		}
	}

	setLoadingState(loading) {
		const buttonText = document.querySelector("[data-button-text]");
		const loadingSpinner = document.querySelector("[data-loading]");

		if (loading) {
			if (buttonText) buttonText.textContent = "Adding...";
			if (loadingSpinner) loadingSpinner.classList.remove("bx-hidden");
			if (this.addToCartBtn) this.addToCartBtn.disabled = true;
		} else {
			if (loadingSpinner) loadingSpinner.classList.add("bx-hidden");

			// Check current variant availability to avoid enabling button while out of stock
			const currentVariant = window.productVariants?.find(
				(v) => v.id == this.currentVariantId,
			);
			const isAvailable = currentVariant?.available ?? true;

			if (buttonText) {
				buttonText.textContent = isAvailable
					? "Add to Cart"
					: "Unavailable";
			}
			if (this.addToCartBtn) {
				this.addToCartBtn.disabled = !isAvailable;
			}
		}
	}

	resetPurchaseButtons() {
		const buyNowBtn = document.querySelector("[data-buy-now]");
		const buyNowText = document.querySelector("[data-buy-now-text]");
		const buyNowLoading = document.querySelector("[data-buy-now-loading]");
		const buttonText = document.querySelector("[data-button-text]");

		const currentVariant = window.productVariants?.find(
			(v) => v.id == this.currentVariantId,
		);
		const isAvailable = currentVariant?.available ?? true;

		if (buyNowText) buyNowText.textContent = "Buy Now";
		if (buyNowLoading) buyNowLoading.classList.add("bx-hidden");
		if (buyNowBtn) buyNowBtn.disabled = !isAvailable;

		if (buttonText) {
			buttonText.textContent = isAvailable
				? "Add to Cart"
				: "Unavailable";
		}
		if (this.addToCartBtn) {
			this.addToCartBtn.disabled = !isAvailable;
		}

		// Update PayPal/dynamic checkout button state
		const paymentButtons = document.querySelector("[data-payment-buttons]");
		if (paymentButtons) {
			if (isAvailable) {
				paymentButtons.classList.remove("payment-button-disabled");
			} else {
				paymentButtons.classList.add("payment-button-disabled");
			}
		}
	}

	openImageModal(src, alt) {
		// Prefer the image-zoom-modal snippet handler if available
		if (typeof window.openImageModal === "function") {
			window.openImageModal(src, alt);
			return;
		}

		// Fallback to modal event system if present
		if (typeof ModalOpenEvent !== "undefined") {
			document.dispatchEvent(
				new ModalOpenEvent("image-zoom-modal", {
					title: alt || "Product Image",
					content: `<img src="${src}" alt="${alt}" class="bx-max-w-full bx-max-h-[80vh] bx-object-contain">`,
					size: "lg",
					customData: { imageSrc: src, imageAlt: alt },
				}),
			);
		}
	}

	showNotification(message, type = "info") {
		// Remove existing notification if any
		const existingNotification = document.querySelector(
			".product-notification",
		);
		if (existingNotification) {
			existingNotification.remove();
		}

		// Create notification element
		const notification = document.createElement("div");

		// For error type, position above .cart-buttons; otherwise use fixed position
		const cartButtons = document.querySelector(".cart-buttons");
		const isError = type === "error";

		if (isError && cartButtons) {
			// Insert notification above cart buttons
			notification.className = `product-notification bx-w-full bx-bg-white bx-border bx-rounded-[8px] bx-p-[12px] bx-shadow-lg bx-mb-[12px] bx-transition-all bx-duration-300 bx-flex bx-items-center bx-gap-[10px]`;
			notification.style.opacity = "0";
			notification.style.transform = "translateY(-10px)";
		} else {
			notification.className = `product-notification bx-fixed bx-top-[20px] bx-right-[20px] bx-bg-white bx-border bx-rounded-[8px] bx-p-[16px] bx-shadow-lg bx-z-50 bx-transition-all bx-duration-300 bx-flex bx-items-center bx-gap-[10px]`;
			notification.style.transform = "translateX(100%)";
			notification.style.opacity = "0";
		}

		// Create icon and message elements
		let iconSvg = "";
		if (type === "success") {
			notification.classList.add(
				"bx-border-green-500",
				"bx-bg-green-50",
				"bx-text-green-700",
			);
			iconSvg = `<svg class="bx-w-5 bx-h-5 bx-flex-shrink-0 bx-text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
			</svg>`;
		} else if (type === "error") {
			notification.classList.add(
				"bx-border-red-500",
				"bx-bg-red-50",
				"bx-text-red-700",
			);
			iconSvg = `<svg class="bx-w-5 bx-h-5 bx-flex-shrink-0 bx-text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
			</svg>`;
		}

		notification.innerHTML = `${iconSvg}<span class="bx-text-[14px] laptop:bx-text-[16px]">${message}</span>`;

		// Insert notification
		if (isError && cartButtons) {
			cartButtons.parentNode.insertBefore(notification, cartButtons);
		} else {
			document.body.appendChild(notification);
		}

		// Animate in
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				notification.style.opacity = "1";
				notification.style.transform =
					isError && cartButtons ? "translateY(0)" : "translateX(0)";
			});
		});

		// Remove after 3 seconds - store timer for cleanup
		this.notificationTimer = setTimeout(() => {
			notification.style.opacity = "0";
			notification.style.transform =
				isError && cartButtons
					? "translateY(-10px)"
					: "translateX(100%)";
			setTimeout(() => {
				notification.remove();
			}, 300);
		}, 3000);
	}

	getVariantMoney(variantId, field) {
		return window.productVariantMoney?.[variantId]?.[field] || null;
	}

	/**
	 * Normalize cart error message for display
	 * Replaces "sold out" with "unavailable" for consistency
	 */
	normalizeCartErrorMessage(message) {
		if (typeof message !== "string") return message;
		return message.replace(/already sold out/gi, "unavailable");
	}

	handleVariantSelected(e) {
		const selectedVariant = e.detail?.variant;
		const productMainSwiper = window.productMainSwiper;
		const productMedia = window.productMedia || [];
		const targetMediaId =
			selectedVariant?.featured_media?.id ||
			selectedVariant?.featured_image?.id;

		if (!selectedVariant || !productMainSwiper || !targetMediaId) return;

		const targetImageIndex = productMedia.findIndex(
			(media) => media.id === targetMediaId,
		);

		if (
			targetImageIndex !== -1 &&
			targetImageIndex !== productMainSwiper.activeIndex
		) {
			productMainSwiper.slideTo(targetImageIndex, 300);
		}
	}

	handleCartUpdate(e) {
		// Handle cart update events

		// Update cart count in header if exists
		const cartCount = document.querySelector("[data-cart-count]");
		if (cartCount && e.detail.item_count !== undefined) {
			cartCount.textContent = e.detail.item_count;
		}

		// Open cart added modal instead of showing notification
		if (e.detail.action === "add") {
			if (e.detail.is_multi && e.detail.products) {
				this.openCartAddedModal({ products: e.detail.products });
			} else if (e.detail.item) {
				this.openCartAddedModal(e.detail);
			}
		}
	}

	/**
	 * Open cart added modal with product info
	 */
	openCartAddedModal(detail) {
		// Multi-product format
		if (detail.products) {
			if (window.cartAddedModal) {
				window.cartAddedModal.open({ products: detail.products });
			}
			return;
		}

		const item = detail.item;
		if (!item) return;

		const variantId = item.variant_id || item.id;
		const formattedPrice = this.getVariantMoney(variantId, "price") || "";

		// Prepare product data for modal
		const productData = {
			image: item.image || item.featured_image?.url || "",
			title: item.product_title || item.title || "",
			variant_title: item.variant_title || "",
			price: formattedPrice,
			quantity: detail.quantity || 1,
		};

		// Open modal if available
		if (window.cartAddedModal) {
			window.cartAddedModal.open(productData);
		} else {
			// Fallback to notification if modal not available
			this.showNotification("Product added to cart!", "success");
		}
	}

	handleCartError(e) {
		// Handle cart error events
		console.error("Cart error:", e.detail);
		this.showNotification(e.detail.error || "Cart error occurred", "error");
	}

	/**
	 * Handle API errors (Shopify captcha/verification page)
	 * Shows a notification and automatically refreshes the page after a delay
	 */
	handleRequestError(response) {
		console.warn(
			`[ProductPage] API Error: ${response.status} - ${response.statusText}`,
		);

		// Reset loading states
		this.setLoadingState(false);
		this.resetPurchaseButtons();

		// Show error notification with refresh hint (uses same style as other cart errors)
		this.showNotification(
			"Add to cart error, please try again after refreshing the page.",
			"error",
		);

		// Auto refresh after delay to show Shopify's verification page
		setTimeout(() => {
			window.location.reload();
		}, 2000);
	}

	handleScroll(e) {
		// Handle scroll events from Lenis
		const scrollY = e.scroll;

		// Example: Sticky add to cart button
		const stickyCart = document.querySelector(".sticky-add-to-cart");
		if (stickyCart) {
			const productForm = document.querySelector(".product-form");
			if (productForm) {
				const formRect = productForm.getBoundingClientRect();
				const shouldShow = formRect.bottom < 0;

				stickyCart.style.transform = shouldShow
					? "translateY(0)"
					: "translateY(100%)";
			}
		}
	}
}

// Make ProductPage available globally
if (typeof window !== "undefined") {
	window.ProductPage = ProductPage;
}
